<template>
  <div class="chart-box">
    <!-- premium trajectory as teacher pay converges to parity -->
    <svg :viewBox="`0 0 ${W} ${H}`" class="chart" @mousemove="onMove" @mouseleave="hover = null">
      <g v-for="t in yTicks" :key="`g${t}`">
        <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(t)" :y2="y(t)" class="grid" />
        <text :x="PAD.l - 8" :y="y(t) + 3.5" class="tick" text-anchor="end">{{ signed(t) }}</text>
      </g>
      <!-- guideline band 50-65% -->
      <rect :x="PAD.l" :y="y(hi)" :width="W - PAD.l - PAD.r" :height="y(lo) - y(hi)" class="band" />
      <text :x="W - PAD.r - 6" :y="y(hi) + 14" class="band-label" text-anchor="end">Guideline +{{ lo }}–{{ hi }}%</text>
      <!-- premium line -->
      <path :d="path" class="series" fill="none" />
      <circle v-for="p in points" :key="p.year" :cx="x(p.year)" :cy="y(p.premium)" r="3.5" class="dot" />
      <!-- annotations -->
      <text :x="x(points[0].year)" :y="y(points[0].premium) - 12" class="anno" text-anchor="start">
        {{ signed(points[0].premium) }} today
      </text>
      <text :x="x(lastPoint.year)" :y="y(lastPoint.premium) + 22" class="anno anno-good" text-anchor="end">
        {{ signed(lastPoint.premium) }} at parity
      </text>
      <text v-for="p in points" :key="`x${p.year}`" :x="x(p.year)" :y="H - PAD.b + 16" class="tick" text-anchor="middle">
        {{ p.year }}
      </text>
      <g v-if="hover">
        <line :x1="x(hover.year)" :x2="x(hover.year)" :y1="PAD.t" :y2="H - PAD.b" class="crosshair" />
        <circle :cx="x(hover.year)" :cy="y(hover.premium)" r="4.5" class="dot-hover" />
      </g>
    </svg>
    <div v-if="hover" class="tooltip">
      <b>{{ hover.year }}</b> — CAT 1b {{ fmtM(hover.teacher) }}, {{ roleLabel }} {{ fmtM(pkg) }} (unchanged)
      → premium {{ signed(hover.premium, 1) }}
    </div>
    <div v-else class="tooltip tt-muted">
      The {{ roleLabel }} package is held flat; only the teacher base moves.
    </div>

    <div class="parity-controls">
      <label>
        CAT 1b reaches
        <input
          type="number" min="7" max="20" step="0.1"
          :value="parityTarget / 1e6"
          @input="parityTarget = Number($event.target.value) * 1e6"
        />
        M VUV
      </label>
      <label>
        by
        <select v-model.number="parityYear">
          <option v-for="yy in parityYearOptions" :key="yy" :value="yy">{{ yy }}</option>
        </select>
      </label>
      <span class="control-note">Australian-parity assumption — adjust to the board's target.</span>
    </div>

    <!-- absolute anchor: the package against the Australian market for the role -->
    <div class="anchor">
      <div class="anchor-title">In Australian dollar terms</div>
      <svg :viewBox="`0 0 ${W} 84`" class="chart">
        <rect :x="ax(0)" y="38" :width="ax(audMax) - ax(0)" height="8" class="anchor-track" />
        <rect :x="ax(AU_RANGE[0])" y="34" :width="ax(AU_RANGE[1]) - ax(AU_RANGE[0])" height="16" class="band" />
        <text :x="(ax(AU_RANGE[0]) + ax(AU_RANGE[1])) / 2" y="26" class="band-label" text-anchor="middle">
          {{ auLabel }} · AUD {{ AU_RANGE[0] / 1000 }}k–{{ AU_RANGE[1] / 1000 }}k
        </text>
        <rect :x="ax(principalAud) - 5" y="32" width="10" height="20" class="anchor-marker" />
        <text :x="ax(principalAud)" y="70" class="anchor-value" text-anchor="middle">
          PVIS · ≈AUD {{ Math.round(principalAud / 1000) }}k
        </text>
        <text v-for="t in [0, 100000, 200000, 300000, 400000]" :key="t" :x="ax(t)" y="82" class="tick" text-anchor="middle">
          {{ t / 1000 }}k
        </text>
      </svg>
      <p class="anchor-note">
        {{ fmtVUV(pkg) }} ≈ AUD {{ Math.round(principalAud / 1000) }}k ≈ USD {{ Math.round(principalUsd / 1000) }}k
        (at {{ AUD_PER_VUV }} AUD/VUV) — against the indicative Australian range for the role.
        Range to be confirmed against an international-school compensation survey.
      </p>
    </div>
  </div>
</template>

<script>
import { scaleLinear, linePath, niceTicks } from '../../utils/chartUtils';
import { formatM, formatVUV } from '../../utils/boardEngine';

const AUD_PER_VUV = 0.0119;
const USD_PER_VUV = 0.00832;

export default {
  name: 'PremiumTrajectory',
  props: {
    teacherPkg2027: { type: Number, required: true }, // CAT 1b today
    pkg: { type: Number, required: true }, // the package held flat
    roleLabel: { type: String, default: 'Principal' },
    lo: { type: Number, default: 50 },
    hi: { type: Number, default: 65 },
    auLo: { type: Number, default: 200000 },
    auHi: { type: Number, default: 400000 },
    auLabel: { type: String, default: 'Australian principal, comparable school' },
  },
  data: () => ({
    W: 1000, H: 260, PAD: { l: 52, r: 24, t: 16, b: 30 },
    hover: null,
    parityTarget: 10_500_000,
    parityYear: 2031,
    AUD_PER_VUV,
  }),
  computed: {
    points() {
      const out = [];
      const y0 = 2027;
      const yEnd = this.parityYear + 1;
      for (let year = y0; year <= yEnd; year++) {
        const f = Math.min(1, (year - y0) / (this.parityYear - y0 || 1));
        const teacher = this.teacherPkg2027 + (this.parityTarget - this.teacherPkg2027) * f;
        out.push({ year, teacher, premium: (this.pkg / teacher - 1) * 100 });
      }
      return out;
    },
    lastPoint() { return this.points[this.points.length - 1]; },
    x() {
      return scaleLinear([this.points[0].year, this.lastPoint.year], [this.PAD.l + 16, this.W - this.PAD.r - 16]);
    },
    yDomain() {
      const vals = [...this.points.map((p) => p.premium), this.lo, this.hi];
      return [Math.min(...vals) - 10, Math.max(...vals) + 12];
    },
    y() { return scaleLinear(this.yDomain, [this.H - this.PAD.b, this.PAD.t]); },
    yTicks() { return niceTicks(this.yDomain[0], this.yDomain[1], 4); },
    path() { return linePath(this.points.map((p) => [this.x(p.year), this.y(p.premium)])); },
    parityYearOptions() {
      const out = [];
      for (let yy = 2029; yy <= 2036; yy++) out.push(yy);
      return out;
    },
    audMax() { return 450000; },
    ax() { return scaleLinear([0, this.audMax], [this.PAD.l, this.W - this.PAD.r]); },
    principalAud() { return this.pkg * AUD_PER_VUV; },
    principalUsd() { return this.pkg * USD_PER_VUV; },
    AU_RANGE() { return [this.auLo, this.auHi]; },
  },
  methods: {
    fmtM: formatM,
    fmtVUV: formatVUV,
    signed(v, d = 0) {
      return `${v >= 0 ? '+' : '\u2212'}${Math.abs(v).toFixed(d)}%`;
    },
    onMove(e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const vx = ((e.clientX - rect.left) / rect.width) * this.W;
      let best = null, bd = Infinity;
      for (const p of this.points) {
        const d = Math.abs(this.x(p.year) - vx);
        if (d < bd) { bd = d; best = p; }
      }
      this.hover = best;
    },
  },
};
</script>

<style scoped>
.chart-box { position: relative; }
.chart { width: 100%; height: auto; display: block; }
.grid { stroke: var(--hairline); stroke-width: 1; }
.tick { font-size: 10.5px; fill: var(--muted); font-variant-numeric: tabular-nums; }
.band { fill: var(--track); stroke: var(--muted); stroke-width: 1; }
.band-label { font-size: 10.5px; font-weight: 700; fill: var(--muted); }
.series { stroke: var(--ink); stroke-width: 2; stroke-linejoin: round; }
.dot { fill: var(--ink); stroke: var(--paper); stroke-width: 2; }
.dot-hover { fill: var(--red); stroke: var(--paper); stroke-width: 2; }
.anno { font-size: 12px; font-weight: 700; fill: var(--red); }
.anno-good { fill: var(--ink); }
.crosshair { stroke: var(--muted); stroke-width: 1; }
.tooltip { margin-top: 4px; font-size: 12px; color: var(--ink); min-height: 18px; }
.tt-muted { color: var(--muted); }
.parity-controls {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  margin: 12px 0 0; font-size: 12px; color: var(--ink);
}
.parity-controls input {
  width: 58px; padding: 3px 6px; font-size: 13px; border: 1px solid var(--ink); border-radius: 0;
  background: var(--paper); color: var(--ink); font-variant-numeric: tabular-nums;
}
.parity-controls select {
  padding: 3px 6px; font-size: 13px; border: 1px solid var(--ink); border-radius: 0;
  background: var(--paper); color: var(--ink);
}
.control-note { font-size: 10.5px; color: var(--muted); }
.anchor { margin-top: 18px; border-top: 1px solid var(--hairline); padding-top: 12px; }
.anchor-title {
  font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--muted); margin-bottom: 6px;
}
.anchor-track { fill: none; stroke: var(--hairline); stroke-width: 1; }
.anchor-marker { fill: var(--ink); }
.anchor-value { font-size: 11.5px; font-weight: 700; fill: var(--ink); font-variant-numeric: tabular-nums; }
.anchor-note { font-size: 11px; color: var(--muted); line-height: 1.55; margin: 6px 0 0; }
</style>
